/**
 * Proposal Review Page
 *
 * Full-screen review of a single proposal packet. Loads by proposalId,
 * shows header, cycle/loop context, contents, rationale/readiness, and
 * Accept / Return for revision / Reject using proposalPacketStore actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileText,
  User,
  Clock,
  CheckCircle2,
  RotateCcw,
  XCircle,
  MessageSquare,
  AlertTriangle,
  ArrowLeft,
  ArrowUp,
  Layers,
} from 'lucide-react';
import BallTrackerStrip from '../../../components/prioritization/catchball/BallTrackerStrip';
import { EvaluatorResponseFrame } from '../../../components/prioritization/catchball/EvaluatorResponseFrame';
import { CallBehindProposal } from '../../../components/prioritization/catchball/CallBehindProposal';
import { useToast } from '../../../components/ui/Toast';
import { buildCatchballInboxUrl } from '../../../lib/catchballInboxUrls';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useProposalPacketStore } from '../../../stores/prioritization/proposalPacketStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { canDecideProposal, canRatifyProposal } from '../../../lib/catchball/proposalDecisions';
import { useCatchballAcceptRight } from '../../../hooks/prioritization/useActRight';
import { refusedByRight } from '../../../lib/actRight';
import { CatchballUndoAction } from '../../../components/prioritization/catchball/CatchballUndoAction';
import { withdrawCatchballProposal } from '../../../lib/reversals/reversalsService';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type {
  CatchballProposal,
  StructuredBlockers,
} from '../../../types/prioritization/catchballProposal';

function StatusBadge({ status }: { status: CatchballProposal['status'] }) {
  switch (status) {
    case 'pending':
    case 'proposed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm font-medium rounded-full">
          <Clock className="h-4 w-4" />
          Awaiting executive response
        </span>
      );
    case 'accepted':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm font-medium rounded-full">
          <CheckCircle2 className="h-4 w-4" />
          Accepted
        </span>
      );
    case 'accepted_provisional':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-medium rounded-full">
          <CheckCircle2 className="h-4 w-4" />
          Accepted (provisional)
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--coral-dim)] text-[var(--coral)] text-sm font-medium rounded-full">
          <XCircle className="h-4 w-4" />
          Rejected
        </span>
      );
    case 'countered':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-sm font-medium rounded-full">
          <RotateCcw className="h-4 w-4" />
          Returned for revision
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-sm font-medium rounded-full">
          {status}
        </span>
      );
  }
}

const emptyBlockers: StructuredBlockers = {
  missingCriteriaLock: false,
  missingRanges: [],
  missingValues: [],
  altitudeMismatch: [],
};

export function ProposalReviewPage() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const fetchProposalById = useProposalPacketStore((s) => s.fetchProposalById);
  const getProposal = useProposalPacketStore((s) => s.getProposal);
  const acceptProvisional = useProposalPacketStore((s) => s.acceptProvisional);
  const returnWithBlockers = useProposalPacketStore((s) => s.returnWithBlockers);
  const rejectProposal = useProposalPacketStore((s) => s.rejectProposal);
  const isLoading = useProposalPacketStore((s) => s.isLoading);
  const error = useProposalPacketStore((s) => s.error);

  const ratifyProposal = useCatchballStore((s) => s.acceptProposal);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const getLoop = useCatchballLoopStore((s) => s.getLoop);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const getItemById = useItemsStore((s) => s.getItemById);
  const userId = useUserStore((s) => s.userId);

  const [proposal, setProposal] = useState<CatchballProposal | null>(null);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [returnMessage, setReturnMessage] = useState('');
  const [rejectMessage, setRejectMessage] = useState('');
  const [blockers, setBlockers] = useState<StructuredBlockers>(emptyBlockers);
  const [ratifying, setRatifying] = useState(false);

  useEffect(() => {
    if (!proposalId) return;
    fetchProposalById(proposalId).then((p) => setProposal(p ?? null));
  }, [proposalId, fetchProposalById]);

  const resolvedProposal = proposal ?? (proposalId ? getProposal(proposalId) : undefined);
  const cycle = resolvedProposal ? cycles.find((c) => c.id === resolvedProposal.cycleId) : undefined;
  const loop = resolvedProposal?.loopId ? getLoop(resolvedProposal.loopId) : undefined;
  // D-4 (migration 987): who may accept — and ratify — is the database's
  // answer, the rule catchball_accept acts on (including the cycle's "Items /
  // selection" decision right when one is set). The Accept controls are drawn
  // only for a person who may use them; otherwise a plain line says why (§2.8).
  const acceptRight = useCatchballAcceptRight(resolvedProposal?.id ?? null);

  useEffect(() => {
    if (resolvedProposal?.cycleId) void fetchLoops(resolvedProposal.cycleId);
  }, [resolvedProposal?.cycleId, fetchLoops]);

  const goAfterDecision = useCallback(
    (opts: { title: string; detail: string }) => {
      showToast('success', opts.title, opts.detail);
      if (resolvedProposal?.cycleId && resolvedProposal.loopId) {
        navigate(buildCatchballInboxUrl(resolvedProposal.cycleId, resolvedProposal.loopId));
        return;
      }
      if (resolvedProposal?.cycleId) {
        navigate(`/prioritization/cascade/inbox/${resolvedProposal.cycleId}`);
      }
    },
    [navigate, resolvedProposal?.cycleId, resolvedProposal?.loopId, showToast],
  );

  const handleAccept = useCallback(async () => {
    if (!resolvedProposal?.id || !userId) return;
    const ok = await acceptProvisional({
      proposalId: resolvedProposal.id,
      responseType: 'accept_provisional',
      actorId: userId,
    });
    if (ok) {
      const refreshed = await fetchProposalById(resolvedProposal.id);
      if (refreshed) setProposal(refreshed);
      goAfterDecision({
        title: 'Proposal accepted',
        detail: 'Continue on Catchball — or open the cycle report to see what was recorded.',
      });
    }
  }, [resolvedProposal, userId, acceptProvisional, fetchProposalById, goAfterDecision]);

  const handleAcceptAndRatify = useCallback(async () => {
    if (!resolvedProposal?.id || !resolvedProposal.loopId || !resolvedProposal.cycleId || !userId) return;
    setRatifying(true);
    try {
      const ok = await ratifyProposal({
        proposalId: resolvedProposal.id,
        loopId: resolvedProposal.loopId,
        cycleId: resolvedProposal.cycleId,
        actorId: userId,
        workspaceId: getCurrentWorkspaceId() ?? '',
      });
      if (ok) {
        void fetchLoops(resolvedProposal.cycleId);
        const refreshed = await fetchProposalById(resolvedProposal.id);
        if (refreshed) setProposal(refreshed);
        showToast(
          'success',
          'Accepted and ratified',
          'Recorded to this cycle’s report.',
        );
        navigate(`/prioritization/cycles/${resolvedProposal.cycleId}/report`);
      } else {
        showToast(
          'error',
          'Not ratified',
          useCatchballStore.getState().error
            || 'Nothing was recorded. Try Accept first, then open Catchball or the cycle report.',
        );
      }
    } finally {
      setRatifying(false);
    }
  }, [
    resolvedProposal,
    userId,
    ratifyProposal,
    fetchLoops,
    fetchProposalById,
    showToast,
    navigate,
  ]);

  const handleReturn = useCallback(async () => {
    if (!resolvedProposal?.id || !userId) return;
    const ok = await returnWithBlockers({
      proposalId: resolvedProposal.id,
      responseType: 'return',
      actorId: userId,
      structuredBlockers: blockers,
      message: returnMessage,
    });
    if (ok) {
      setShowReturnForm(false);
      setReturnMessage('');
      setBlockers(emptyBlockers);
      goAfterDecision({
        title: 'Returned for revision',
        detail: 'The holders can take the ball on Catchball and run another sitting.',
      });
    }
  }, [resolvedProposal, userId, blockers, returnMessage, returnWithBlockers, goAfterDecision]);

  const handleReject = useCallback(async () => {
    if (!resolvedProposal?.id || !userId) return;
    const ok = await rejectProposal({
      proposalId: resolvedProposal.id,
      responseType: 'reject',
      actorId: userId,
      message: rejectMessage,
    });
    if (ok) {
      setShowRejectForm(false);
      setRejectMessage('');
      goAfterDecision({
        title: 'Proposal rejected',
        detail: 'Catchball shows the closed exchange for this loop.',
      });
    }
  }, [resolvedProposal, userId, rejectMessage, rejectProposal, goAfterDecision]);

  if (!proposalId) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-[var(--text-sec)]">Missing proposal ID.</p>
        <Link to="/prioritization/catchball" className="mt-4 inline-flex items-center gap-2 text-[var(--text-ter)] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Catchball
        </Link>
      </div>
    );
  }

  if (isLoading && !resolvedProposal) {
    return (
      <div className="px-4 py-12 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--slate)] border-t-transparent" />
        <p className="mt-4 text-[var(--text-sec)]">Loading proposal…</p>
      </div>
    );
  }

  if (!resolvedProposal) {
    return (
      <div className="px-4 py-8 text-center">
        <FileText className="h-12 w-12 text-[var(--text-ter)] mx-auto" />
        <p className="mt-4 text-[var(--text-sec)]">Proposal not found.</p>
        <Link to="/prioritization/catchball" className="mt-4 inline-flex items-center gap-2 text-[var(--text-ter)] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Catchball
        </Link>
      </div>
    );
  }

  const snapshot = resolvedProposal.readinessSnapshot;
  // Mirrors the server's decision gate (763): pending / proposed / acknowledged.
  const isPending = canDecideProposal(resolvedProposal.status);
  const isAccepted =
    resolvedProposal.status === 'accepted' || resolvedProposal.status === 'accepted_provisional';
  const cycleId = resolvedProposal.cycleId;
  const inboxHref =
    cycleId && resolvedProposal.loopId
      ? buildCatchballInboxUrl(cycleId, resolvedProposal.loopId)
      : null;
  const backHref = inboxHref ?? (cycleId ? `/prioritization/catchball?cycleId=${cycleId}` : '/prioritization/catchball');

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      {/* Back / context */}
      <div className="mb-6">
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {inboxHref ? 'Back to Catchball' : 'Back to Catchball'}
        </Link>
      </div>

      {cycleId && (
        <div className="mb-6">
          <BallTrackerStrip loop={loop ?? null} cycleId={cycleId} currentUserId={userId ?? undefined} />
        </div>
      )}

      {isAccepted && (
        <section className="mb-6 border border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)] dark:bg-[var(--green)]/25 p-5 space-y-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                Accepted — what happens next
              </h2>
              <p className="text-sm text-[var(--green)]/90 dark:text-[var(--text-ter)]/90 mt-0.5">
                This is a Catchball packet (not the Governance → Proposals list). Continue on Catchball,
                or open the cycle report. To write the cycle report, use Accept &amp; ratify when the
                proposal is still open — or ratify from the exchange if this accept was provisional-only.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {inboxHref && (
              <Link
                to={inboxHref}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[var(--green)] text-white text-sm font-medium hover:bg-[var(--green)]"
              >
                <ArrowUp className="h-4 w-4" />
                Open Catchball
              </Link>
            )}
            {cycleId && (
              <Link
                to={`/prioritization/cycles/${cycleId}/report`}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)]/80 dark:bg-[var(--surface)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm font-medium"
              >
                <FileText className="h-4 w-4" />
                Cycle report
              </Link>
            )}
            {cycleId && (
              <Link
                to={`/prioritization/cascade/reconcile/${cycleId}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)]/80 dark:bg-[var(--surface)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm font-medium"
              >
                <Layers className="h-4 w-4" />
                Reconcile / Cycle report
              </Link>
            )}
            {resolvedProposal.loopId && cycleId && acceptRight.allowed && canRatifyProposal(resolvedProposal.status, loop?.state) && (
              <button
                type="button"
                disabled={ratifying}
                onClick={() => void handleAcceptAndRatify()}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--slate-dim)] disabled:opacity-50"
              >
                <Layers className="h-4 w-4" />
                {ratifying ? 'Recording…' : 'Record to cycle report'}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Evaluator judgment surface — intent vs response, with Accept / Revise / Reject */}
      {loop?.intent && isPending && (
        <div className="mb-6">
          <EvaluatorResponseFrame
            originalIntent={loop.intent}
            response={{
              proposals: resolvedProposal ? [resolvedProposal] : [],
              synthesis: loop.synthesis?.summary,
              rationale: resolvedProposal?.rationale,
            }}
            isLoading={isLoading}
            onAccept={acceptRight.allowedProvisional ? () => void handleAccept() : null}
            acceptLine={acceptRight.line}
            mayDecide={!refusedByRight(acceptRight)}
            onRevise={(guidance) => {
              setReturnMessage(guidance);
              void handleReturn();
            }}
            onReject={(reason) => {
              setRejectMessage(reason);
              void handleReject();
            }}
          />
        </div>
      )}

      {/* Header */}
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-ter)] mb-0.5">
                Exchange Response Review
              </p>
              <h1 className="text-xl font-semibold text-[var(--text-pri)]">
                Proposal packet{loop != null ? ` — Loop ${loop.loopNumber}` : ''}
              </h1>
              <p className="mt-1 text-sm text-[var(--text-ter)] font-mono">{resolvedProposal.id}</p>
            </div>
            <StatusBadge status={resolvedProposal.status} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[var(--text-sec)]">
            {cycle && <span>Cycle: {cycle.name ?? cycle.id}</span>}
            {loop && <span>Loop: {loop.loopNumber}</span>}
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {resolvedProposal.proposedByName || resolvedProposal.proposedBy || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Submitted {resolvedProposal.proposedAt ? new Date(resolvedProposal.proposedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)] flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Ranking packaged with the proposal */}
          {(resolvedProposal.aggregatedRankList?.length ?? 0) > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] uppercase tracking-wide mb-2">
                Proposed ranking
              </h2>
              <ol className="border border-[var(--rule)] divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
                {resolvedProposal.aggregatedRankList!.map((itemId, index) => {
                  const title = getItemById(itemId)?.title;
                  return (
                    <li
                      key={itemId}
                      className="flex items-baseline gap-3 px-4 py-2.5 text-sm"
                    >
                      <span className="w-8 shrink-0 font-mono text-base text-[var(--text-ter)]">
                        #{index + 1}
                      </span>
                      <span className="text-[var(--text-pri)]">
                        {title ?? `${itemId.slice(0, 8)}…`}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* Rationale */}
          <section>
            <h2 className="text-sm font-semibold text-[var(--text-pri)] uppercase tracking-wide mb-2">Rationale</h2>
            <p className="text-[var(--text-sec)] whitespace-pre-wrap">
              {resolvedProposal.rationale || 'No rationale provided.'}
            </p>
          </section>

          {/* Packet contents */}
          {(resolvedProposal.objectiveIds?.length || resolvedProposal.ostNodeIds?.length) ? (
            <section>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] uppercase tracking-wide mb-2">Proposal packet contents</h2>
              <div className="bg-[var(--surface)] p-4 space-y-3 text-sm">
                {resolvedProposal.objectiveIds && resolvedProposal.objectiveIds.length > 0 && (
                  <div>
                    <span className="font-medium text-[var(--text-sec)]">Objectives: </span>
                    <span className="text-[var(--text-sec)]">{resolvedProposal.objectiveIds.join(', ')}</span>
                  </div>
                )}
                {resolvedProposal.ostNodeIds && resolvedProposal.ostNodeIds.length > 0 && (
                  <div>
                    <span className="font-medium text-[var(--text-sec)]">OST nodes: </span>
                    <span className="text-[var(--text-sec)]">{resolvedProposal.ostNodeIds.join(', ')}</span>
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {/* Readiness context */}
          {snapshot && (
            <section>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] uppercase tracking-wide mb-2">Readiness context</h2>
              <div className="bg-[var(--surface)] p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-sec)]">Total items</span>
                  <span className="font-medium">{snapshot.summary.totalItems}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-sec)]">Ready</span>
                  <span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">{snapshot.summary.itemsReady}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-sec)]">With gaps</span>
                  <span className="font-medium text-[var(--mustard)]">{snapshot.summary.itemsWithGaps}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-sec)]">Criteria locked</span>
                  <span className={snapshot.summary.missingCriteriaLock ? 'text-[var(--coral)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                    {snapshot.summary.missingCriteriaLock ? 'No' : 'Yes'}
                  </span>
                </div>
                {snapshot.summary.missingRanges && snapshot.summary.missingRanges.length > 0 && (
                  <div className="pt-2 border-t border-[var(--rule)] text-[var(--mustard)]">
                    Missing ranges: {snapshot.summary.missingRanges.join(', ')}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Return form */}
          {showReturnForm && (
            <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4 space-y-4">
              <h3 className="font-medium text-[var(--text-sec)]">{CATCHBALL_LABELS.returnForRevision}</h3>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={blockers.missingCriteriaLock}
                  onChange={(e) => setBlockers((b) => ({ ...b, missingCriteriaLock: e.target.checked }))}
                  className="rounded border-[var(--rule)]"
                />
                <span className="text-sm">Criteria must be locked</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Message (optional)</label>
                <textarea
                  value={returnMessage}
                  onChange={(e) => setReturnMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                  placeholder="Add context or questions..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReturn}
                  disabled={isLoading}
                  className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50"
                >
                  Confirm return
                </button>
                <button
                  type="button"
                  onClick={() => setShowReturnForm(false)}
                  className="px-4 py-2 text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {showRejectForm && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 space-y-4">
              <h3 className="font-medium text-[var(--coral)]">Reject proposal</h3>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Reason (required)</label>
                <textarea
                  value={rejectMessage}
                  onChange={(e) => setRejectMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                  placeholder="Why is this proposal being rejected?"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={isLoading || !rejectMessage.trim()}
                  className="px-4 py-2 bg-[var(--coral)] text-white text-sm font-medium hover:bg-[var(--coral)] disabled:opacity-50"
                >
                  Confirm reject
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-2 text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* The call behind what is about to be accepted: a standing is never drawn alone */}
        {isPending && !showReturnForm && !showRejectForm && (
          <div className="px-6 py-4 border-t border-[var(--rule)]">
            <CallBehindProposal proposal={resolvedProposal} />
          </div>
        )}

        {/* Actions */}
        {isPending && !showReturnForm && !showRejectForm && (
          <div className="px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)] flex flex-wrap gap-3">
            {acceptRight.line && (
              <p className="basis-full text-sm text-[var(--text-ter)]">{acceptRight.line}</p>
            )}
            {acceptRight.allowedProvisional && (
              <button
                onClick={() => void handleAccept()}
                disabled={isLoading || ratifying}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green)] text-white font-medium hover:bg-[var(--green)] disabled:opacity-50"
              >
                <CheckCircle2 className="h-5 w-5" />
                {CATCHBALL_LABELS.accept}
              </button>
            )}
            {resolvedProposal.loopId && acceptRight.allowed && (
              <button
                type="button"
                onClick={() => void handleAcceptAndRatify()}
                disabled={isLoading || ratifying}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50"
                title="Accept and write the cycle report in one step"
              >
                <Layers className="h-5 w-5" />
                {ratifying ? 'Recording…' : CATCHBALL_LABELS.acceptAndRatify}
              </button>
            )}
            {/* 1001: rejecting and returning exercise the right accepting does — not drawn when it shuts this person out (build contract §2.8) */}
            {!refusedByRight(acceptRight) && (
              <>
                <button
                  onClick={() => setShowReturnForm(true)}
                  disabled={isLoading || ratifying}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50"
                >
                  <MessageSquare className="h-5 w-5" />
                  {CATCHBALL_LABELS.returnForRevision}
                </button>
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={isLoading || ratifying}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--coral)] text-[var(--coral)] font-medium hover:bg-[var(--coral-dim)] disabled:opacity-50"
                >
                  <XCircle className="h-5 w-5" />
                  {CATCHBALL_LABELS.reject}
                </button>
              </>
            )}
          </div>
        )}

        {/* Withdraw — mine to take back while nobody above has decided yet */}
        {isPending && !showReturnForm && !showRejectForm && userId && resolvedProposal.proposedBy === userId && (
          <div className="px-6 pb-4 bg-[var(--surface)]">
            <CatchballUndoAction
              triggerLabel="Withdraw proposal"
              modalTitle="Withdraw this proposal"
              description="Nobody above has decided on it yet — withdrawing returns it to you before it was submitted."
              confirmLabel="Withdraw proposal"
              reasonPlaceholder="Why withdraw this proposal? (required)"
              onConfirm={(reason) => withdrawCatchballProposal(resolvedProposal.id, reason)}
              onSuccess={async () => {
                const refreshed = await fetchProposalById(resolvedProposal.id);
                if (refreshed) setProposal(refreshed);
              }}
              successTitle="Proposal withdrawn"
              successMessage="It is back with you, unsubmitted."
              className="border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
              stopPropagation={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ProposalReviewPage;
